import { useState, useEffect, useMemo } from 'react';

import BpkButton from '@skyscanner-internal/backpack-web/bpk-component-button';
import {
  BpkSpacing,
  BpkStack,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import { useSelector, useDispatch } from 'react-redux';

import getFeatureDecision from '../../../../config';
import { useLocalisation } from '../../../../services/contextAdaptor/LocalisationContext';
import { useItineraryVisibility } from '../../../App/ItineraryVisibilityContext';
import { confirmSelectedResult } from '../../actions/result-select-actions';
import { CTA_VERTICAL } from '../../helpers/constants/analyticsConstants';
import { DEPARTURE_TIME_BOUNDARIES_MINUTES } from '../../helpers/constants/departureTimeConstants';
import { useTranslation } from '../../helpers/hooks/useTranslation';
import { trackInteractionEvent } from '../../helpers/logging/trackInteractionEvent';
import { trackCTASmartMetric } from '../../middleware/trackEvent';
import {
  getPreferDirects,
  getSelectedInboundDate,
  getSelectedOutboundDate,
  getTripDuration,
  getCheapestCombinations,
  getTranslatedString,
  getDepartureTimesSelected,
  isReturn,
} from '../../selectors';
import getSelectedDaysPrice from '../../selectors/selectedDaysPrice';

import type { DepartureTime } from '../../helpers/constants/departureTimeConstants';
import type { State } from '../../selectors/types';

import './TripTotalCostFlightSummary.scss';

const earliestDepartureMinutes = (departureTimes: DepartureTime[]): number => {
  if (departureTimes.length === 0) return 0; // none selected = any time, start at midnight
  return DEPARTURE_TIME_BOUNDARIES_MINUTES[departureTimes[0]].start;
};

const latestDepartureMinutes = (departureTimes: DepartureTime[]): number => {
  if (departureTimes.length === 0) return 1439; // none selected = any time, end at 11:59
  return DEPARTURE_TIME_BOUNDARIES_MINUTES[
    departureTimes[departureTimes.length - 1]
  ].end;
};

const TripTotalCostFlightSummary = () => {
  const dispatch = useDispatch();
  const translate = useTranslation();
  const { formatCurrency } = useLocalisation();

  const shouldShowDealHunterLite = getFeatureDecision(
    'ENABLE_DEAL_HUNTER_LITE',
    false,
  );
  // Selectors
  const outboundDate = useSelector(getSelectedOutboundDate);
  const inboundDate = useSelector(getSelectedInboundDate);
  const preferDirects = useSelector(getPreferDirects);
  const returnTrip = useSelector(isReturn);
  const tripDuration = useSelector(getTripDuration);
  const cheapestCombinations = useSelector(getCheapestCombinations);
  const departureTimes = useSelector(getDepartureTimesSelected);

  const departureTimeRange =
    returnTrip || departureTimes.length === 0
      ? undefined
      : `${earliestDepartureMinutes(departureTimes)}-${latestDepartureMinutes(departureTimes)}`;

  const cheapestCombo = useMemo(() => {
    if (!tripDuration || !cheapestCombinations) return null;
    return cheapestCombinations[tripDuration] ?? null;
  }, [tripDuration, cheapestCombinations]);

  // Get all journey type translations
  const returnDirectFlight = useSelector((state: State) =>
    getTranslatedString(state, 'return_direct_dot_flight'),
  );
  const returnFlight = useSelector((state: State) =>
    getTranslatedString(state, 'return_flight'),
  );
  const oneWayDirect = useSelector((state: State) =>
    getTranslatedString(state, 'one_way_direct'),
  );
  const oneWay = useSelector((state: State) =>
    getTranslatedString(state, 'one_way'),
  );
  const { isItineraryVisible } = useItineraryVisibility();

  // Calculate journey type
  const journeyType = useMemo(() => {
    if (returnTrip) {
      return preferDirects ? returnDirectFlight : returnFlight;
    }
    return preferDirects ? oneWayDirect : oneWay;
  }, [
    returnTrip,
    preferDirects,
    returnDirectFlight,
    returnFlight,
    oneWayDirect,
    oneWay,
  ]);

  // Use the same price selector as the calendar so the CTA always matches
  // the price shown for the selected date(s), including when directness
  // and/or departure time filters are applied.
  const price = useSelector(getSelectedDaysPrice);

  // Format prices
  const priceFormatted = price ? formatCurrency(price) : null;

  const ctaText =
    shouldShowDealHunterLite && isItineraryVisible
      ? translate('mv_cheapestDates_dealHunter_find_more_flights_cta')
      : translate('find_flights_text');

  const groupAriaLabel = useSelector((state: State) =>
    getTranslatedString(state, 'ktxt_cta_from_price_per_adult', {
      price: priceFormatted || '',
    }),
  );

  // Cheapest combinations logic
  const cheapestCombinationsList = useMemo(
    () =>
      cheapestCombinations
        ? Object.values(cheapestCombinations).filter((combo) => combo !== null)
        : [],
    [cheapestCombinations],
  );

  const isCheapestCombinationSelected = Boolean(tripDuration && cheapestCombo);
  const [userSelectedCombination, setUserSelectedCombination] = useState(
    isCheapestCombinationSelected,
  );
  const [selectionIsCombination, setSelectionIsCombination] = useState(false);

  useEffect(() => {
    const outboundDateIndex = outboundDate.getDate() - 1;
    const inboundDateIndex = inboundDate ? inboundDate.getDate() - 1 : null;
    const isCombination = cheapestCombinationsList.some((combo) => {
      const isOutboundMatch = combo?.outboundIndex === outboundDateIndex;
      const isInboundMatch = combo?.inboundIndex === inboundDateIndex;
      return isOutboundMatch && isInboundMatch;
    });
    setSelectionIsCombination(isCombination);
  }, [outboundDate, inboundDate, cheapestCombinationsList]);

  useEffect(() => {
    if (isCheapestCombinationSelected) {
      setUserSelectedCombination(true);
    }
  }, [isCheapestCombinationSelected]);

  const handleResultConfirmed = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    trackCTASmartMetric(CTA_VERTICAL.FLIGHTS);

    event.preventDefault();
    event.stopPropagation();

    // Logging Interaction Event in addition to legacy events
    trackInteractionEvent({
      name: 'Flights_CTA_Clicked',
      strings: {
        ...(tripDuration != null && {
          tripDuration: tripDuration.toString(),
        }),
        ...(departureTimeRange && { departureTimes: departureTimes.join(',') }),
      },
      bools: {
        cheapestCombinationSelected: isCheapestCombinationSelected,
        userSelectedCombination,
        selectionIsCombination,
      },
    });
    dispatch(
      confirmSelectedResult(
        outboundDate,
        inboundDate,
        preferDirects,
        departureTimeRange,
      ),
    );
  };

  return (
    <BpkStack direction="column" gap={BpkSpacing.None}>
      <BpkStack
        direction="row"
        justify="space-between"
        align="center"
        gap={BpkSpacing.SM}
        wrap={{ base: 'wrap', tablet: 'nowrap' }}
        aria-label={groupAriaLabel}
        role="group"
        data-testid="month-view-variant__trip-summary"
      >
        <BpkStack
          direction="column"
          flexGrow={1}
          gap={BpkSpacing.SM}
          paddingEnd={BpkSpacing.Base}
        >
          <BpkText textStyle={TEXT_STYLES.label1}>
            {translate('flights_tab_title')}
          </BpkText>
          <BpkText
            textStyle={TEXT_STYLES.footnote}
            color={TEXT_COLORS.textSecondary}
          >
            {journeyType}
          </BpkText>
        </BpkStack>

        {priceFormatted && (
          <BpkStack direction="column" align="flex-end" gap={BpkSpacing.SM}>
            <BpkText textStyle={TEXT_STYLES.label1}>
              {shouldShowDealHunterLite && isItineraryVisible
                ? translate('mv_from_price', { price: priceFormatted })
                : priceFormatted}
            </BpkText>
            <BpkText
              textStyle={TEXT_STYLES.footnote}
              className="month-view-variant-total-trip-cost__price-description"
              color={TEXT_COLORS.textSecondary}
            >
              {translate('per_adult_label')}
            </BpkText>
          </BpkStack>
        )}
      </BpkStack>
      <BpkStack
        direction="column"
        gap={BpkSpacing.SM}
        width="100%"
        paddingTop={BpkSpacing.MD}
      >
        <BpkButton
          className="month-view-variant-total-trip-cost__cta-button-experiment-rhr"
          onClick={handleResultConfirmed}
          data-newrelic-synthetic="mvp-flights-cta"
          data-testid="flights-cta"
        >
          {ctaText}
        </BpkButton>
      </BpkStack>
    </BpkStack>
  );
};

export default TripTotalCostFlightSummary;
